import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BlockIcon from "@mui/icons-material/Block";
import CachedIcon from "@mui/icons-material/Cached";
import FilterListIcon from "@mui/icons-material/FilterList";
import LockIcon from "@mui/icons-material/Lock";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonIcon from "@mui/icons-material/Person";
import SearchOff from "@mui/icons-material/SearchOff";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslationHook } from "../../hooks";
import { User } from "../../types/model/user";

interface Props {
  tableData: User[];
  onRequestRefresh: () => void;
  onRowClick: (user: User) => void;
}

const UsersTable = ({
  tableData,
  onRequestRefresh, 
  onRowClick,
}: Props ) => {
  const { t } = useTranslationHook();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const getUserIcon = (user: User) => {
    let IconComponent = PersonIcon;
    let color = "text.secondary";
    let statusKey;

    if (!user.enabled || user.locked) {
      IconComponent = LockIcon;
      color = "error.main";
      statusKey = "generic.locked";
    }
    else if (user.enabled) {
      statusKey = "generic.enabled";

      switch (user.roleType) {
        case "ADMIN":
          IconComponent = AdminPanelSettingsIcon;
          color = "primary.dark";
          break;

        case "ACCOUNT_MANAGER":
          IconComponent = ManageAccountsIcon;
          color = "primary.main";
          break;

        case "ACCOUNT_USER":
          IconComponent = PersonIcon;
          color = "text.primary";
          break;
      }
    }
    else {
      IconComponent = BlockIcon;
      color = "error.main";
      statusKey = "generic.disabled";
    }

    const roleLabel = t(`admin.users.roles.${user.roleType}`);

    return (
      <Tooltip title={`${roleLabel} – ${t(statusKey)}`} arrow>
        <IconComponent sx={{ color }} />
      </Tooltip>
    );
  };

  const filteredData = tableData.filter((user) => {
    let matches = true;
    for (const key in filters) {
      if (filters[key]) {
        const filterValue = String(filters[key]).toLowerCase();
        const userValue = String(user[key as keyof User]).toLowerCase();
        if (!userValue.includes(filterValue)) {
          matches = false;
          break;
        }
      }
    }
    return matches;
  });

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  const hasData = visibleRows.length > 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("admin.users.table.title")}
          </Typography>

          {/* Filter button */}
          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          {/* Refresh button */}
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {showFilters && (
          <Grid2 container spacing={3} sx={{ mb: 2 }}>
            <Grid2 size={12}>
              <TextField
                name="name"
                label={t("generic.search")}
                value={filters.name ?? ""}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.users.table.name")}</TableCell>
                <TableCell>{t("admin.users.table.email")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasData ? (
                <>
                  {visibleRows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      onClick={() => onRowClick(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getUserIcon(row)}
                          <Typography variant="body2">{row.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("admin.users.table.no-users")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default UsersTable;
